import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Icon } from "../../design-system/primitives/Icon";

/** Sitio corporativo de Axon Group. Mismo destino que el diálogo About. */
const AXON_GROUP_URL = "https://axongroup.com.co/";
/** Logotipo oficial servido desde public/ (sin petición externa ni CORS). */
const AXON_GROUP_LOGO_URL = `${import.meta.env.BASE_URL}axon-group-logo.svg`;

/**
 * Tarjeta promocional renderizada en la página de inicio, bajo el panel
 * de modos. Responde una pregunta concreta -- "¿qué más hace Axon Group
 * además de esta herramienta?" -- con un texto breve y dos sublistas
 * (Productos / Servicios) tomadas del catálogo oficial de axongroup.com.co.
 *
 * Replica el diseño de la tarjeta original de axon-protocol-analizer:
 * superficie con contorno discreto (sin rellenos de color primario),
 * listas en monoespaciada y opacidades suaves, de modo que se lea como
 * un recurso útil y no como un anuncio. Los tokens de color del proyecto
 * de referencia (`primary`, `surface-container-low`, `outline-variant`)
 * se mapean a las variables CSS equivalentes de esta app (`--color-brand`,
 * `--color-surface-container-low`, `--color-outline-variant`).
 */
export function AxonResourcesCard() {
  const { t } = useTranslation("brand");
  return (
    <a
      href={AXON_GROUP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group block w-full max-w-xl rounded-lg border border-[color-mix(in_srgb,var(--color-outline-variant)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-surface-container-low)_40%,transparent)] p-4 transition-colors hover:border-[color-mix(in_srgb,var(--color-brand)_40%,transparent)] hover:bg-[var(--color-surface-container-low)] focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-brand)_40%,transparent)]"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <img
            src={AXON_GROUP_LOGO_URL}
            alt="Axon Group"
            className="h-7 w-auto select-none opacity-90 transition-opacity group-hover:opacity-100"
            draggable={false}
          />
          <Icon
            name="open_in_new"
            size={14}
            className="flex-shrink-0 text-[var(--color-on-surface-variant)] transition-colors group-hover:text-[var(--color-brand)]"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          <p className="text-[12px] leading-[1.0625rem] text-[var(--color-on-surface-variant)]">
            <Trans
              i18nKey="card.description"
              ns="brand"
              components={{
                url: <span className="text-[var(--color-brand)] group-hover:underline" />,
              }}
            />
          </p>
          <ResourceSection title={t("card.sections.products")}>
            <ResourceItem name={t("card.products.axonTest.name")}>
              {t("card.products.axonTest.description")}
            </ResourceItem>
            <ResourceItem name={t("card.products.at61.name")}>
              {t("card.products.at61.description")}
            </ResourceItem>
            <ResourceItem name={t("card.products.axonBuilder.name")}>
              {t("card.products.axonBuilder.description")}
            </ResourceItem>
            <ResourceItem name={t("card.products.gatewayExchange.name")}>
              {t("card.products.gatewayExchange.description")}
            </ResourceItem>
          </ResourceSection>
          <ResourceSection title={t("card.sections.services")}>
            <ResourceItem>{t("card.services.automation")}</ResourceItem>
            <ResourceItem>{t("card.services.digital")}</ResourceItem>
            <ResourceItem>{t("card.services.cybersec")}</ResourceItem>
          </ResourceSection>
        </div>
      </div>
    </a>
  );
}

function ResourceSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wider text-[var(--color-on-surface-variant)]">
        {title}
      </span>
      <ul className="flex flex-col gap-0.5 font-mono text-[10px] text-[color-mix(in_srgb,var(--color-on-surface-variant)_80%,transparent)]">
        {children}
      </ul>
    </div>
  );
}

function ResourceItem({ name, children }: { name?: string; children: ReactNode }) {
  return (
    <li className="flex items-start gap-1.5">
      <Icon
        name="arrow_right"
        size={12}
        className="mt-px flex-shrink-0 text-[color-mix(in_srgb,var(--color-brand)_70%,transparent)]"
      />
      <span className="min-w-0 flex-1">
        {name !== undefined ? (
          <>
            <span className="text-[var(--color-on-surface-variant)]">{name}</span>
            {" — "}
          </>
        ) : null}
        {children}
      </span>
    </li>
  );
}
